import { creatorbase } from '@round/api';
import { getTableMetaHelper, Image, Skeleton, SkeletonizedValue } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import styles from './ReportsTable.module.css';
import moment from 'moment';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import { getUserNameInitials } from 'Modules/Plans/User/user.helpers';
import { Dropdown, assets } from '@round/whitelabel-ui';
import { VirtualizedTable, VirtualizedTableProps as VirtualTableProps } from '@round/whitelabel-ui';
import { TooltipOnOverflow } from '@round/whitelabel-ui';

const { Edit: EditIcon, Trash: TrashIcon } = assets;

export type ReportsTableRow = creatorbase.Report & {
    assignees: creatorbase.User[];
};

type Props = Pick<VirtualTableProps<ReportsTableRow>, 'data' | 'onEndReached'> & {
    isLoading?: boolean;
    isAssigneesLoading?: (reportId: creatorbase.Report['id']) => boolean;
    hasError?: boolean;
    onEditReport: (report: ReportsTableRow) => void;
    onDeleteReport: (report: ReportsTableRow) => void;
};

type TableCellContext<TKey extends keyof ReportsTableRow> = CellContext<ReportsTableRow, ReportsTableRow[TKey]>;

type Meta = Pick<Props, 'isLoading' | 'isAssigneesLoading' | 'onEditReport' | 'onDeleteReport'>;
const getTableMeta = getTableMetaHelper<Meta>();

const ReportsTable = ({
    data,
    hasError,
    isLoading,
    isAssigneesLoading,
    onEditReport,
    onEndReached,
    onDeleteReport,
}: Props) => {
    const navigate = useNavigate();

    const columns: ColumnDef<ReportsTableRow, any>[] = [
        {
            header: 'Report name',
            accessorKey: 'name',
            meta: {
                className: styles.reportCell,
            },
            cell: ({ getValue, table, row: { original } }: TableCellContext<'name'>) => {
                const { isLoading } = getTableMeta(table);

                return (
                    <div className={styles.report}>
                        <Image
                            className={styles.reportImage}
                            skeletonCircle={false}
                            src={original.image || ''}
                            loading={isLoading}
                        />

                        <div className={styles.titles}>
                            <SkeletonizedValue width="10rem" isInitialized={!isLoading}>
                                <TooltipOnOverflow tooltip={<p className={styles.tooltip}>{getValue()}</p>}>
                                    <p className={styles.title}>{getValue()}</p>
                                </TooltipOnOverflow>
                            </SkeletonizedValue>
                            <SkeletonizedValue width="7rem" isInitialized={!isLoading}>
                                <TooltipOnOverflow tooltip={<p className={styles.tooltip}>{original.description}</p>}>
                                    <p className={styles.subTitle}>{original.description || '-'}</p>
                                </TooltipOnOverflow>
                            </SkeletonizedValue>
                        </div>
                    </div>
                );
            },
        },
        {
            header: 'Created',
            accessorKey: 'created_at',
            cell: ({ getValue, table }: TableCellContext<'created_at'>) => {
                const { isLoading } = getTableMeta(table);

                if (isLoading) {
                    return <Skeleton />;
                }

                return moment(getValue()).format('D MMMM YYYY');
            },
        },
        {
            header: 'Assignees',
            accessorKey: 'assignees',
            cell: ({ getValue, table, row: { original } }: TableCellContext<'assignees'>) => {
                const { isLoading, isAssigneesLoading } = getTableMeta(table);
                const users = getValue();
                const isRowAssigneesLoading = isAssigneesLoading?.(original.id) ?? false;

                if (isLoading || isRowAssigneesLoading) {
                    return <Skeleton />;
                }

                return (
                    <div className={styles.teamContainer}>
                        {users.map((user, index) => (
                            <CircleBadge key={index} tooltip={user.name}>
                                {getUserNameInitials(user.name)}
                            </CircleBadge>
                        ))}
                    </div>
                );
            },
        },
        {
            header: '',
            id: 'actions',
            meta: {
                className: styles.actions,
            },
            cell: ({ row: { original }, table }) => {
                const { isLoading, onEditReport, onDeleteReport } = getTableMeta(table);

                if (isLoading) {
                    return <Skeleton />;
                }

                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            icon={EditIcon}
                                            onClick={() => {
                                                onEditReport(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Edit report
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                            icon={() => <TrashIcon className={styles.deleteAction} />}
                                            onClick={() => {
                                                onDeleteReport(original);
                                                setIsOpen(false);
                                            }}
                                            className={styles.deleteAction}
                                        >
                                            Delete Report
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const meta: Meta = {
        isLoading,
        isAssigneesLoading,
        onEditReport,
        onDeleteReport,
    };

    const emptyRows = new Array(10).fill({}) as ReportsTableRow[];
    const rows = isLoading ? data.concat(emptyRows) : data;

    return (
        <VirtualizedTable
            isLoading={isLoading}
            containerClassName={styles.tableContainer}
            className={styles.table}
            data={rows}
            columns={columns}
            meta={meta}
            onRowClick={(row) => {
                if (isLoading) {
                    return;
                }

                navigate(`${row.original.id}/`);
            }}
            noDataLabel={
                <div aria-label="no reports" className={styles.noDataContainer}>
                    {hasError ? "Couldn't fetch reports" : 'No reports found'}
                </div>
            }
            virtualizerOptions={{
                count: rows.length,
                estimateSize: () => 50,
                getItemKey: (index) => rows[index]?.id || index,
                overscan: 5,
            }}
            onEndReached={onEndReached}
        />
    );
};

export default ReportsTable;
